export type SiteLanguage = 'en' | 'de' | 'el' | 'bg';

export const languages: Array<{ code: SiteLanguage; label: string; nativeLabel: string; flag: string }> = [
  { code: 'en', label: 'English', nativeLabel: 'English', flag: '/assets/flags/en.svg' },
  { code: 'de', label: 'German', nativeLabel: 'Deutsch', flag: '/assets/flags/de.svg' },
  { code: 'el', label: 'Greek', nativeLabel: 'Ελληνικά', flag: '/assets/flags/el.svg' },
  { code: 'bg', label: 'Bulgarian', nativeLabel: 'Български', flag: '/assets/flags/bg.svg' },
];

export const defaultLanguage: SiteLanguage = 'en';
export const pathLanguages: SiteLanguage[] = ['bg', 'de', 'el'];

export function getLanguageFromValue(value: string | null | undefined): SiteLanguage {
  if (value === 'de' || value === 'el' || value === 'bg' || value === 'en') {
    return value;
  }
  return defaultLanguage;
}

export function getLanguageFromPath(pathname: string): SiteLanguage | null {
  const firstSegment = pathname.split('/').filter(Boolean)[0];
  return firstSegment === 'bg' || firstSegment === 'de' || firstSegment === 'el' ? firstSegment : null;
}

export function stripLanguagePrefix(pathname: string): string {
  const parts = pathname.split('/').filter(Boolean);
  if (parts[0] === 'bg' || parts[0] === 'de' || parts[0] === 'el') {
    parts.shift();
  }
  return `/${parts.join('/')}`.replace(/\/$/, '') || '/';
}

export function withLanguagePrefix(pathname: string, language: SiteLanguage): string {
  const cleanPath = stripLanguagePrefix(pathname);
  if (language === defaultLanguage) return cleanPath;
  return `/${language}${cleanPath === '/' ? '' : cleanPath}`;
}

export const uiText: Record<SiteLanguage, Record<string, string>> = {
  en: {
    newsTitle: 'News & Promotions',
    newsSubtitle: 'Stay up to date with the latest news, service updates, awards, and exclusive promotions from World Transport Overseas.',
    latestUpdates: 'Latest Updates',
    blogTitle: 'Blog',
    blogSubtitle: 'Insights, guides, and practical logistics knowledge from World Transport Overseas.',
    latestArticles: 'Latest Articles',
    home: 'Home',
    all: 'All',
    featured: 'Featured',
    readFullStory: 'Read full story',
    readMore: 'Read more',
    noArticles: 'No articles found for this category.',
    manage: 'Manage',
    language: 'Language',
  },
  de: {
    newsTitle: 'Neuigkeiten & Aktionen',
    newsSubtitle: 'Bleiben Sie auf dem Laufenden über aktuelle Nachrichten, Service-Updates, Auszeichnungen und exklusive Aktionen von World Transport Overseas.',
    latestUpdates: 'Aktuelle Meldungen',
    blogTitle: 'Blog',
    blogSubtitle: 'Einblicke, Leitfäden und praktisches Logistikwissen von World Transport Overseas.',
    latestArticles: 'Aktuelle Artikel',
    home: 'Startseite',
    all: 'Alle',
    featured: 'Empfohlen',
    readFullStory: 'Ganze Meldung lesen',
    readMore: 'Mehr lesen',
    noArticles: 'Für diese Kategorie wurden keine Artikel gefunden.',
    manage: 'Verwalten',
    language: 'Sprache',
  },
  el: {
    newsTitle: 'Νέα & Προσφορές',
    newsSubtitle: 'Μείνετε ενημερωμένοι για τα τελευταία νέα, τις ενημερώσεις υπηρεσιών, τις διακρίσεις και τις αποκλειστικές προσφορές της World Transport Overseas.',
    latestUpdates: 'Τελευταίες ενημερώσεις',
    blogTitle: 'Blog',
    blogSubtitle: 'Αναλύσεις, οδηγοί και πρακτική γνώση logistics από τη World Transport Overseas.',
    latestArticles: 'Τελευταία άρθρα',
    home: 'Αρχική',
    all: 'Όλα',
    featured: 'Προτεινόμενο',
    readFullStory: 'Διαβάστε ολόκληρο το άρθρο',
    readMore: 'Περισσότερα',
    noArticles: 'Δεν βρέθηκαν άρθρα για αυτήν την κατηγορία.',
    manage: 'Διαχείριση',
    language: 'Γλώσσα',
  },
  bg: {
    newsTitle: 'Новини и промоции',
    newsSubtitle: 'Следете последните новини, актуализации на услуги, отличия и ексклузивни промоции от World Transport Overseas.',
    latestUpdates: 'Последни новини',
    blogTitle: 'Блог',
    blogSubtitle: 'Анализи, ръководства и практични логистични знания от World Transport Overseas.',
    latestArticles: 'Последни статии',
    home: 'Начало',
    all: 'Всички',
    featured: 'Водеща',
    readFullStory: 'Прочети цялата новина',
    readMore: 'Прочети още',
    noArticles: 'Няма намерени статии в тази категория.',
    manage: 'Управление',
    language: 'Език',
  },
};
